import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaUsers, FaCheck, FaArrowRight } from 'react-icons/fa';
import './Ladakh.css';

const Ladakh = () => {
  const ladakhTours = [
    {
      id: 'leh-leh',
      name: 'Leh Ladakh (Leh - Leh)',
      duration: '05 Nights 06 Days',
      groupSize: '10-15 Group Size',
      price: '₹ 18,500/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1755005645/ltsn8qmpjzob4bqr8568.jpg',
      highlights: [
        'Bike adventure through Nubra Valley',
        'Cross Khardung La Pass',
        'Visit Pangong Tso Lake',
        'Explore Turtuk Village'
      ],
      path: '/leh-ladakh-tour'
    },
    {
      id: 'turtuk',
      name: 'Leh Ladakh (Turtuk)',
      duration: '06 Nights 07 Days',
      groupSize: '10-15 Group Size',
      price: '₹ 22,500/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1755005586/kyxnsozatwyjceruq7gq.jpg',
      highlights: [
        'Extended Turtuk exploration',
        'K2 Mountain range views',
        'Shyok River experience',
        'LOC border village visit'
      ],
      path: '/leh-ladakh-turtuk-tour'
    },
    {
      id: 'zanskar',
      name: 'Leh Ladakh (Zanskar)',
      duration: '07 Nights 08 Days',
      groupSize: '10-15 Group Size',
      price: '₹ 25,500/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1755006004/ncdqs0fuabbqnrju42lx.jpg',
      highlights: [
        'Zanskar Valley exploration',
        'Chadar Trek experience',
        'Monastery visits',
        'Frozen river adventure'
      ],
      path: '/leh-ladakh-zanskar-tour'
    },
    {
      id: 'srinagar-route',
      name: 'Leh Ladakh (Srinagar Route)',
      duration: '08 Nights 09 Days',
      groupSize: '10-15 Group Size',
      price: '₹ 28,500/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1755005586/mxbscatfnhae0v68vxvj.jpg',
      highlights: [
        'Manali to Leh route',
        'Srinagar exploration',
        'Multiple high passes',
        'Complete circuit experience'
      ],
      path: '/leh-ladakh-srinagar-tour'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Leh Ladakh Tours - NomadTrailz</title>
        <meta name="description" content="Explore the breathtaking landscapes of Leh Ladakh with our adventure tours - from Nubra Valley to Pangong Tso Lake." />
      </Helmet>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        {/* Hero Section */}
        <section className="ladakh-hero">
          <div className="hero-background">
            <img src="https://res.cloudinary.com/duym2bgp2/image/upload/v1755005586/mxbscatfnhae0v68vxvj.jpg" alt="Leh Ladakh" />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.8 }}
            >
              Leh Ladakh Tours
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Choose your preferred Leh Ladakh adventure
            </motion.p>
          </div>
        </section>

        {/* Tours Section */}
        <section className="ladakh-tours">
          <div className="container">
            <div className="tours-grid">
              {ladakhTours.map((tour, index) => (
                <motion.div
                  key={tour.id}
                  className="tour-card"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                >
                  <div className="tour-image">
                    <img src={tour.image} alt={tour.name} />
                  </div>
                  <div className="tour-content">
                    <h3>{tour.name}</h3>
                    <div className="tour-details">
                      <div className="detail-item">
                        <FaCalendarAlt className="detail-icon" />
                        <span>{tour.duration}</span>
                      </div>
                      <div className="detail-item">
                        <FaUsers className="detail-icon" />
                        <span>{tour.groupSize}</span>
                      </div>
                    </div>
                    <div className="tour-price">
                      <span className="price-label">Starting From</span>
                      <span className="price-amount">{tour.price}</span>
                    </div>
                    <div className="tour-highlights">
                      {tour.highlights.map((highlight, idx) => (
                        <div key={idx} className="highlight-item">
                          {highlight}
                        </div>
                      ))}
                    </div>
                    <Link to={tour.path} className="view-details-btn">
                      View Details
                      <FaArrowRight className="btn-icon" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="ladakh-about">
          <div className="container">
            <motion.div 
              className="about-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2>About Leh Ladakh</h2>
              <p>
                Leh Ladakh, often called the "Land of High Passes," is a region of breathtaking beauty 
                in the northernmost part of India. Known for its dramatic landscapes, ancient monasteries, 
                and unique culture, Ladakh offers an unparalleled adventure experience. The region is 
                characterized by its high-altitude desert, crystal-clear lakes, and snow-capped mountains 
                that create a surreal and mesmerizing environment.
              </p>
              <p>
                Our Leh Ladakh tours are designed to showcase the best of this incredible region:
              </p>
              <ul>
                <li><strong>Leh - Leh Route:</strong> Classic circuit covering Nubra Valley, Pangong Tso, and Khardung La Pass</li>
                <li><strong>Turtuk Extension:</strong> Explore the northernmost village of India with K2 mountain views</li>
                <li><strong>Zanskar Adventure:</strong> Experience the frozen river trek and ancient monasteries</li>
                <li><strong>Srinagar Route:</strong> Complete circuit from Manali to Leh via Srinagar with multiple high passes</li>
              </ul>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Ladakh;
